import React, { useState } from 'react';
import { Search, Filter, ShoppingCart } from 'lucide-react';
import EmptyState from '../EmptyState';
import type { Sale } from '../../types/sale';

interface SalesListProps {
  sales: Sale[];
  onSelect: (sale: Sale | null) => void;
}

const SalesList: React.FC<SalesListProps> = ({ sales, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.id.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (sales.length === 0) {
    return (
      <EmptyState
        title="Nenhuma venda registrada"
        description="Registre sua primeira venda para começar a acompanhar seu faturamento."
        action={
          <button
            onClick={() => onSelect(null)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            Registrar Primeira Venda
          </button>
        }
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-100">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Buscar vendas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Status</option>
              <option value="pending">Pendentes</option>
              <option value="paid">Pagas</option>
              <option value="cancelled">Canceladas</option>
            </select>
            <button className="p-2 hover:bg-gray-100 rounded-md">
              <Filter className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {filteredSales.map((sale) => (
          <div
            key={sale.id}
            onClick={() => onSelect(sale)}
            className="p-4 hover:bg-gray-50 cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  Venda #{sale.id}
                </p>
                <p className="font-medium">
                  {sale.customer?.name || 'Cliente não identificado'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold">
                  R$ {sale.total.toFixed(2)}
                </p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  getStatusColor(sale.status)
                }`}>
                  {sale.status === 'paid' ? 'Paga' :
                   sale.status === 'pending' ? 'Pendente' : 'Cancelada'}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
              <div>
                {new Date(sale.createdAt).toLocaleDateString('pt-BR')}
              </div>
              <div>
                {sale.paymentMethod === 'cash' ? 'Dinheiro' :
                 sale.paymentMethod === 'credit_card' ? 'Cartão de Crédito' :
                 sale.paymentMethod === 'debit_card' ? 'Cartão de Débito' :
                 sale.paymentMethod === 'pix' ? 'PIX' :
                 sale.paymentMethod === 'bank_slip' ? 'Boleto' : 'Crediário'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SalesList;